import type { DataProvider, GetListParams, GetOneParams } from 'react-admin';

import { requirePartner } from '@src/providers/dataProvider/shared';

import { fetchTradeins } from './fetchTradeins';
import type { FetchTradeinsFilters, TradeinRecord, TriState } from './types';

// The list filter values react-admin threads through (it types `filter` as `any`); narrowing
// here keeps the mapping below type-checked, so a renamed `source` at least fails loudly at the
// one end it can. It does not close the loop: the `source` strings live in TradeinList, and
// keeping the two in step is still a convention rather than something the compiler checks.
//
// One key per rendered input, no more. The query accepts a dozen other arguments, but a mapping
// with no input behind it is only reachable by hand-editing the URL, and an unexercised filter
// path is one nobody notices has broken.
interface TradeinFilter {
  state?: string;
  uuidPrefix?: string;
  id?: string | number;
  tradeinSource?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  city?: string;
  sendbackState?: string;
  hasEscalations?: string;
}

// Drop blank text filters. react-admin leaves a cleared input as '' rather than removing the
// key, and trimming matters too: '  ' is truthy, so it would be sent as-is and match nothing,
// which reads as "no results" rather than "no filter".
const text = (value: string | undefined): string | undefined => {
  const trimmed = value?.trim();
  return trimmed === '' ? undefined : trimmed;
};

// Coerce a free-text id filter. Only positive integers survive, because the server applies these
// under `if (id)` — so a 0 is dropped server-side and the unfiltered list comes back under a
// filter chip that still reads "Trade-in ID: 0". Note the zero cases are not exotic:
// `Number('  ')` and `Number(null)` are both 0, as is a literal 0 from the NumberInput.
const toPositiveId = (value: string | number | undefined): number | undefined => {
  if (value == null) return undefined;
  const parsed = Number(String(value).trim());
  // The upper bound matters as much as the lower one: these go out as `Int`, and anything past
  // 2^31-1 fails variable coercion, which aborts the whole query rather than returning no rows.
  const isSendable = Number.isInteger(parsed) && parsed > 0 && parsed <= 2_147_483_647;
  return isSendable ? parsed : undefined;
};

// The yes/no SelectInput carries 'true'/'false'; the API wants its tri-state Int. Two distinct
// failures to avoid: forwarding the raw string fails `Int` coercion and aborts the whole query,
// so picking "Has escalations: Yes" errors the page out instead of filtering it; and mapping
// 'false' to 0 rather than 2 fails silently, because the resolver tests `=== 1` / `=== 2`, so
// every trade-in comes back while the chip still claims a "No" filter is applied.
const toTriState = (value: string | undefined): TriState | undefined => {
  if (value === 'true') return 1;
  if (value === 'false') return 2;
  return undefined;
};

const toApiFilters = (params: GetListParams): FetchTradeinsFilters => {
  const { page = 1, perPage = 20 } = params.pagination ?? {};
  const filter = (params.filter ?? {}) as TradeinFilter;

  return {
    partnerId: requirePartner().id,
    page,
    pageSize: perPage,
    state: text(filter.state),
    uuidPrefix: text(filter.uuidPrefix),
    id: toPositiveId(filter.id),
    tradeinSource: text(filter.tradeinSource),
    email: text(filter.email),
    firstName: text(filter.firstName),
    lastName: text(filter.lastName),
    city: text(filter.city),
    sendbackState: text(filter.sendbackState),
    hasEscalations: toTriState(filter.hasEscalations),
  };
};

/**
 * Read-only provider: only getList and getOne are supported. The remaining DataProvider methods
 * are stubbed to throw so an unsupported call fails with a clear message rather than an opaque
 * "undefined is not a function" from inside react-admin — the cast at the bottom of this file
 * advertises all of them whether they exist or not.
 */
interface TradeinsDataProvider {
  getList: (
    resource: string,
    params: GetListParams
  ) => Promise<{ data: TradeinRecord[], total: number }>;
  getOne: (
    resource: string,
    params: GetOneParams
  ) => Promise<{ data: TradeinRecord }>;
  getMany: () => Promise<never>;
  getManyReference: () => Promise<never>;
  create: () => Promise<never>;
  update: () => Promise<never>;
  updateMany: () => Promise<never>;
  delete: () => Promise<never>;
  deleteMany: () => Promise<never>;
}

const tradeinsDataProviderImpl: TradeinsDataProvider = {
  getList: async (_resource, params) => {
    const result = await fetchTradeins(toApiFilters(params));
    return { data: result.rows, total: result.count };
  },

  getOne: async (_resource, params) => {
    // Validate before querying. The server applies the id under `if (id)`, so both a NaN (which
    // serializes as null) and a 0 leave an unfiltered query — the partner's newest trade-in then
    // comes back, passes the not-found check below, and is returned as the record that was asked
    // for, carrying its supplier's name, email, phone and street address. `Number.isInteger`
    // alone is not enough: `Number('')` and `Number(null)` are both 0.
    const tradeinId = toPositiveId(params.id);
    if (tradeinId === undefined) {
      throw new Error(`Trade-in ${params.id} not found`);
    }

    const result = await fetchTradeins({
      partnerId: requirePartner().id,
      page: 1,
      pageSize: 1,
      id: tradeinId,
    });
    if (!result.rows.length) {
      throw new Error(`Trade-in ${params.id} not found`);
    }
    return { data: result.rows[0] };
  },

  getMany: async () => { throw new Error('getMany not implemented for tradeins resource'); },
  getManyReference: async () => { throw new Error('getManyReference not implemented for tradeins resource'); },
  create: async () => { throw new Error('create not implemented for tradeins resource'); },
  update: async () => { throw new Error('update not implemented for tradeins resource'); },
  updateMany: async () => { throw new Error('updateMany not implemented for tradeins resource'); },
  delete: async () => { throw new Error('delete not implemented for tradeins resource'); },
  deleteMany: async () => { throw new Error('deleteMany not implemented for tradeins resource'); },
};

// React-admin's DataProvider methods are generic, which prevents a type-safe single-resource
// provider; we define proper types above and cast here.
export const tradeinsDataProvider = tradeinsDataProviderImpl as unknown as DataProvider;
